import * as t from 'io-ts';
import {mapValues} from 'lodash';

import {makeSafeArrayType} from './types/arrayTypes';

const RAuthorityPermissionGrants = t.type({
  everyone: t.boolean,
  teammateIds: makeSafeArrayType(t.number),
  circleIds: makeSafeArrayType(t.number),
});

const RAuthorityPermissionSet = t.type({
  id: t.string,
  grants: RAuthorityPermissionGrants,
  permissions: makeSafeArrayType(t.string),
});

export const RAuthorityPermissions = t.type({
  granular: t.record(t.string, RAuthorityPermissionGrants),
  permissionSets: t.record(t.string, RAuthorityPermissionSet),
});

export type AuthorityPermissionSet = t.TypeOf<typeof RAuthorityPermissionSet>;
export type AuthorityPermissionGrants = t.TypeOf<typeof RAuthorityPermissionGrants>;
export type AuthorityPermissions = t.TypeOf<typeof RAuthorityPermissions>;

function parseGrants(src: any): AuthorityPermissionGrants {
  return {
    everyone: src.everyone,
    teammateIds: src.teammate_ids,
    circleIds: src.circle_ids,
  };
}

function parsePermissionSet(src: any, id: string): AuthorityPermissionSet {
  return {
    id,
    grants: parseGrants(src.grantees),
    permissions: src.permissions,
  };
}

export function parseAuthorityPermissions(srcGranular: any, srcPermissionSets: any): AuthorityPermissions {
  return {
    granular: srcGranular ? mapValues(srcGranular, parseGrants) : {},
    permissionSets: srcPermissionSets
      ? mapValues(srcPermissionSets, (value, key) => parsePermissionSet(value, key))
      : {},
  };
}

/*
 * Mutations.
 */

const RAuthorityPermissionEveryoneGrantee = t.type({
  type: t.literal('everyone'),
});

const RAuthorityPermissionCircleGrantee = t.type({
  type: t.literal('circle'),
  circleId: t.number,
});

const RAuthorityPermissionTeammateGrantee = t.type({
  type: t.literal('teammate'),
  teammateId: t.number,
});

const RAuthorityPermissionGrantee = t.union([
  RAuthorityPermissionEveryoneGrantee,
  RAuthorityPermissionCircleGrantee,
  RAuthorityPermissionTeammateGrantee,
]);

const RAuthorityPermissionChange = t.type({
  permission: t.string,
  grantee: RAuthorityPermissionGrantee,
});

export const RAuthorityPermissionDiff = t.type({
  add: makeSafeArrayType(RAuthorityPermissionChange),
  remove: makeSafeArrayType(RAuthorityPermissionChange),
});
